/**
 * Wertia Dossier Finanzanalyse — utility frontend.
 * Format CHF stile svizzero (delegato a /lib/format.js), helper API, hook useDossier.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { fmtChf as _sharedFmtChf, parseSwissNumber } from "@/lib/format";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/** Token JWT presente in localStorage (chiave del CRM: crm_token). */
function authHeaders() {
  const t = localStorage.getItem("crm_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Formatta CHF stile svizzero (Ticino): "CHF 70'000,00".
 *  Delega all'helper centralizzato in /lib/format.js. */
export const fmtCHF = _sharedFmtChf;

/** Parse input numerico CH (accetta sia 1'234.56 che 1234,56). */
export const parseCHFInput = parseSwissNumber;

/** Formatta percentuale 2 decimali (virgola decimale). */
export function fmtPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00%";
  return `${n.toFixed(2).replace(".", ",")}%`;
}

/** Hook: carica il dossier di un cliente, gestisce update parziali e KPI. */
export function useDossier(clientId) {
  const [dossier, setDossier] = useState(null);
  const [computed, setComputed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadDossier = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/dossiers/by-client/${clientId}`, { headers: authHeaders() });
      setDossier(res.data);
      // Carica anche i KPI calcolati
      if (res.data?.id) {
        try {
          const kpi = await axios.get(`${API}/dossiers/${res.data.id}/computed`, { headers: authHeaders() });
          setComputed(kpi.data);
        } catch (e) { /* non-blocking */ }
      }
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadDossier(); }, [loadDossier]);

  const updateSection = useCallback(async (sectionKey, sectionData) => {
    if (!dossier?.id) return;
    try {
      setSaving(true);
      const payload = { [sectionKey]: sectionData };
      const res = await axios.patch(`${API}/dossiers/${dossier.id}`, payload, { headers: authHeaders() });
      setDossier(res.data);
      // Refresh KPI
      try {
        const kpi = await axios.get(`${API}/dossiers/${dossier.id}/computed`, { headers: authHeaders() });
        setComputed(kpi.data);
      } catch (e) { /* non-blocking */ }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      throw e;
    } finally {
      setSaving(false);
    }
  }, [dossier]);

  const reimportContratti = useCallback(async () => {
    if (!dossier?.id) return;
    try {
      setSaving(true);
      await axios.post(`${API}/dossiers/${dossier.id}/reimport-contratti`, {}, { headers: authHeaders() });
      await loadDossier();
    } finally {
      setSaving(false);
    }
  }, [dossier, loadDossier]);

  return { dossier, computed, loading, saving, error, updateSection, reimportContratti, loadDossier };
}

/** Label IT per tipi di contratto. */
export const CONTRATTO_LABELS = {
  previdenza_vincolata_3a: "Previdenza vincolata 3a",
  previdenza_libera_3b: "Previdenza libera 3b",
  cassa_malati: "Cassa malati",
  mobilia_domestica: "Mobilia domestica",
  responsabilita_civile_privata: "Responsabilità civile privata",
  veicoli_motore: "Veicoli a motore",
  natante: "Natante",
  protezione_giuridica: "Protezione giuridica",
  oggetti_valore: "Oggetti di valore",
  vacanze_viaggi: "Vacanze/Viaggi",
  immobili: "Immobili",
  inventario_aziendale: "Inventario aziendale",
  responsabilita_civile_professionale: "Responsabilità civile professionale",
  protezione_giuridica_aziendale: "Protezione giuridica aziendale",
  registrazione_libera: "Registrazione libera",
  assicurazione_base_lamal: "Assicurazione di base LAMal",
  complemento_infortunio_oamal: "Complemento infortunio OAMal",
  estero_previdenza_statale: "Estero - previdenza statale",
  estero_previdenza_professionale: "Estero - previdenza professionale",
  estero_previdenza_privata: "Estero - previdenza privata",
};

/** Lista compatta dei 5 tipi principali + Registrazione libera per il modal "Nuovo contratto". */
export const CONTRATTO_TIPI_FORM = [
  "previdenza_vincolata_3a",
  "previdenza_libera_3b",
  "cassa_malati",
  "responsabilita_civile_privata",
  "veicoli_motore",
  "registrazione_libera",
];

/** Label IT per i 14 moduli del Barometro. */
export const MODULI_BAROMETRO = [
  { key: "basi", titolo: "BASI", sottotitolo: "Consulenza globale" },
  { key: "budget_denaro_sufficiente", titolo: "BUDGET", sottotitolo: "Avere denaro a sufficienza per uscite" },
  { key: "budget_reddito_elevato", titolo: "BUDGET", sottotitolo: "Reddito più elevato/attività accessoria" },
  { key: "imposte_ottimizzare", titolo: "IMPOSTE", sottotitolo: "Ottimizzare le imposte" },
  { key: "pensionamento_anticipato", titolo: "PENSIONAMENTO", sottotitolo: "Pensionamento anticipato" },
  { key: "pensionamento_tenore_vita", titolo: "PENSIONAMENTO", sottotitolo: "Mantenere il tenore di vita" },
  { key: "rischi_assicurare_reddito", titolo: "RISCHI", sottotitolo: "Assicurare il reddito" },
  { key: "alloggio_proprieta", titolo: "ALLOGGIO", sottotitolo: "Proprietà d'abitazioni" },
  { key: "patrimonio_costituzione", titolo: "PATRIMONIO", sottotitolo: "Costituzione del patrimonio" },
  { key: "polizze_copertura", titolo: "POLIZZE", sottotitolo: "Copertura assicurativa" },
  { key: "rischi_copertura_famiglia", titolo: "RISCHI", sottotitolo: "Copertura per la famiglia" },
  { key: "budget_formazione_continua", titolo: "BUDGET", sottotitolo: "Formazione continua" },
  { key: "budget_risparmiare_figli", titolo: "BUDGET", sottotitolo: "Risparmiare per i figli" },
];

export { API, authHeaders };
